// hooks/useWebSocket.ts
import { useState, useRef, useEffect, useCallback } from 'react';

interface WebSocketOptions {
url?: string;
  autoReconnect?: boolean;
  reconnectInterval?: number; // ms
}

export function useChatSocket({
  url,
  autoReconnect = true,
  reconnectInterval = 3000, // Reduced interval
}: WebSocketOptions) {
  const [status, setStatus] = useState<'Disconnected' | 'Connecting' | 'Connected' | 'Error'>(
    'Disconnected'
  );
  const [message, setMessage] = useState<any>(null);
  const wsRef = useRef<WebSocket | null>(null);
 const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messageQueueRef = useRef<any[]>([]); // Queue messages while connecting

  const connect = useCallback(() => {
    if (!url) return; // Don't connect if no URL
    
    try {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      setStatus('Connecting');
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('Connected');
        
        // Send any queued messages
        while (messageQueueRef.current.length > 0) {
          const queuedMessage = messageQueueRef.current.shift();
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(queuedMessage));
          }
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setMessage(data);
        } catch (e) {
          console.error('Invalid WS message:', event.data);
        }
      };

      ws.onclose = (event) => {
        setStatus('Disconnected');
        wsRef.current = null;
        if (autoReconnect && event.code !== 1000) { // Don't reconnect if manually closed
          reconnectRef.current = setTimeout(connect, reconnectInterval);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WS Error:', error);
        setStatus('Error');
      };
    } catch (error) {
      console.error('❌ WS Connection Error:', error);
      setStatus('Error');
    }
  }, [url, autoReconnect, reconnectInterval]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
    }
    messageQueueRef.current = []; // Clear queue
    setStatus('Disconnected');
  }, []);

  const sendMessage = useCallback((data: any) => {
    try {
      if (wsRef.current && status === 'Connected') {
        wsRef.current.send(JSON.stringify(data));
      } else if (status === 'Connecting') {
        // Queue message while connecting
        messageQueueRef.current.push(data);
      } else {
        console.warn('⚠️ WebSocket not connected, cannot send');
      }
    } catch (error) {
      console.warn(error, '⚠️ WebSocket send error');
    }
  }, [status]);

  useEffect(() => {
    if (url) {
      connect();
    }
    return () => disconnect();
  }, [url, connect, disconnect]);

  return {
    status,
    message,
    sendMessage,
    disconnect,
    reconnect: connect,
  };
}